import { ProjectRenderer } from '../ProjectRenderer';
import {
	UserDataProp,
	UserDataObjectType,
	CustomObjectName,
	CollidableObjectSerializeData,
} from '../types';
import { Box3, Box3Helper, FrontSide, Mesh, MeshStandardMaterial } from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { CENTER } from 'three-mesh-bvh';
import { CollidableObject } from './CollidableObject';
import { Asset } from '@/api/AssetApi';
import { UUID } from '@/types/types';
import RENDERER_CONFIG from '@/configs/rendererConfig';

export interface CreatorProductModuleSerializeData {
	code: string;
	asset?: Asset;
	refId: UUID;
}

export interface SerializedProductModule extends CollidableObjectSerializeData {
	creatorProductModuleData: CreatorProductModuleSerializeData;
}

export class ProductModule extends CollidableObject {
	private creatorProductModuleData: CreatorProductModuleSerializeData;
	css2dObject!: CSS2DObject;

	constructor(
		renderer: ProjectRenderer,
		creatorProductModuleData: CreatorProductModuleSerializeData
	) {
		super(renderer, creatorProductModuleData.refId);

		this.creatorProductModuleData = creatorProductModuleData;
	}

	private initLabel() {
		const objectLength = this.boundingBox.max.z - this.boundingBox.min.z;

		const container = document.createElement('div');
		container.className = 'renderer-popover';
		container.innerText = this.creatorProductModuleData.code;

		this.css2dObject = new CSS2DObject(container);
		this.css2dObject.position.setZ(
			this.collisionsEnabled ? objectLength / 2 : objectLength
		);

		this.group.add(this.css2dObject);

		this.css2dObject.visible = this.renderer.modulesManager.areCodesVisible;
	}

	async loadAsset(src: string) {
		const model = await this.assetsLib.getModel(src);

		model.traverse((child) => {
			child.userData[UserDataProp.OBJECT_TYPE] =
				UserDataObjectType.COLLIDABLE_OBJECT;
			child.userData[UserDataProp.OBJECT_ID] = this.id;
			child.layers.set(RENDERER_CONFIG.COLLIDABLE_MODULE.LAYER);
			child.castShadow = true;
			child.receiveShadow = true;
		});

		this.group.add(model);

		const collisionZoneMesh = this.group.getObjectByName(
			CustomObjectName.ZONE_OUTER
		) as Mesh;

		if (!collisionZoneMesh) {
			this.collisionsEnabled = false;

			this.boundingBox = new Box3().setFromObject(this.group, true);
			this.boundingBoxHelper = new Box3Helper(this.boundingBox, 0xff0000);
			if (import.meta.env.DEV) this.renderer.scene.add(this.boundingBoxHelper);

			this.initLabel();

			return;
		}

		const material = new MeshStandardMaterial({
			color: RENDERER_CONFIG.COLORS.GREEN,
			transparent: true,
			opacity: 0.2,
			side: FrontSide,
		});
		collisionZoneMesh.material = material;

		collisionZoneMesh.geometry.computeBoundsTree({
			maxLeafTris: 1,
			strategy: CENTER,
		});

		this.collisionZoneMesh = collisionZoneMesh;

		this.boundingBox = new Box3().setFromObject(this.collisionZoneMesh, true);
		this.boundingBox.expandByVector(
			RENDERER_CONFIG.COLLISIONS.BOX3_EXPAND_VECTOR
		);

		this.boundingBoxHelper = new Box3Helper(this.boundingBox, 0xff0000);
		if (import.meta.env.DEV) this.renderer.scene.add(this.boundingBoxHelper);

		//LEGACY
		const LEGACY_innerShape = this.group.getObjectByName('zone_inner');
		if (LEGACY_innerShape) LEGACY_innerShape.visible = false;

		this.initLabel();
		this.validateStatus();
	}

	override dispose() {
		super.dispose();
		this.css2dObject.element.remove();
	}

	export(): SerializedProductModule {
		return {
			collisionsEnabled: this.collisionsEnabled,
			creatorProductModuleData: this.creatorProductModuleData,
			isCorrect: this.isCorrect,
			position: [this.group.position.x, this.group.position.z],
			rotation: this.group.rotation.y,
			collidedModules: Array.from(this.collidedModules),
		};
	}
}
